import { useQuery } from '@tanstack/react-query';
import { ArrowPathIcon, ChartBarIcon, CurrencyDollarIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { Navigate } from 'react-router-dom';
import { fetchAdminDashboard } from '../lib/apiClient';
import { useAuth } from '../lib/auth';
import type { AdminDashboardPoint } from '../types';

const numberFormatter = new Intl.NumberFormat('en-US');
const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const chartPlaceholder: AdminDashboardPoint[] = Array.from({ length: 7 }).map((_, index) => ({
  label: `Day ${index + 1}`,
  value: 0,
}));

export function AdminDashboardPage(): JSX.Element {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: fetchAdminDashboard,
    staleTime: 1000 * 60,
    enabled: Boolean(isSuperAdmin),
  });

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/app" replace />;
  }

  const totals = data?.totals;
  const revenuePoints = data?.revenueTrend.points?.length ? data.revenueTrend.points : chartPlaceholder;
  const cards = [
    {
      title: 'Total page views',
      value: totals ? numberFormatter.format(totals.pageViews) : '—',
      icon: ArrowPathIcon,
    },
    {
      title: 'Total revenue',
      value: totals ? currencyFormatter.format(totals.revenue) : '—',
      icon: CurrencyDollarIcon,
    },
    {
      title: 'Total paying users',
      value: totals ? numberFormatter.format(totals.payingUsers) : '—',
      icon: UserGroupIcon,
    },
    {
      title: 'Total signups',
      value: totals ? numberFormatter.format(totals.signups) : '—',
      icon: ChartBarIcon,
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Super admin</p>
          <h1 className="text-2xl font-semibold text-foreground">Platform overview</h1>
          <p className="text-sm text-muted-foreground">Centralised health metrics across InspectAI.</p>
        </div>
        <button
          type="button"
          className="btn-glass btn-secondary focus-ring inline-flex items-center gap-2 text-sm font-semibold"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? 'Refreshing...' : 'Refresh metrics'}
        </button>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <article key={card.title} className="glass-card flex h-full flex-col justify-between gap-4 rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{card.title}</p>
                <span className="rounded-full bg-primary/10 p-2 text-primary">
                  <Icon className="h-5 w-5" />
                </span>
              </div>
              <p className="text-2xl font-semibold text-foreground">{isLoading ? '…' : card.value}</p>
              {card.title === 'Total signups' && totals?.computed?.signups && (
                <p className="text-xs text-muted-foreground">Computed signups: {numberFormatter.format(totals.computed.signups)}</p>
              )}
              {card.title === 'Total paying users' && totals?.computed?.payingUsers && (
                <p className="text-xs text-muted-foreground">Active subscriptions: {numberFormatter.format(totals.computed.payingUsers)}</p>
              )}
            </article>
          );
        })}
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <article className="glass-card lg:col-span-2">
          <div className="flex items-center justify-between border-b border-white/10 px-6 pb-4 pt-5">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Revenue (Last 7 days)</h2>
              <p className="text-sm text-muted-foreground">Track weekly performance across InspectAI.</p>
            </div>
          </div>
          <div className="px-6 py-6">
            <div className="grid gap-3 sm:grid-cols-7">
              {revenuePoints.map((point) => (
                <div key={point.label} className="flex flex-col items-center gap-2">
                  <div className="h-28 w-full overflow-hidden rounded-xl bg-white/40 dark:bg-white/5">
                    <div
                      className="h-full w-full origin-bottom bg-gradient-to-t from-primary/70 to-primary/20 transition"
                      style={{ transform: `scaleY(${Math.min(point.value / 1000, 1)})` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-muted-foreground">{point.label}</span>
                  <span className="text-xs text-foreground">{currencyFormatter.format(point.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </article>

        <article className="glass-card flex flex-col">
          <div className="border-b border-white/10 px-6 pb-4 pt-5">
            <h2 className="text-lg font-semibold text-foreground">Top sources</h2>
            <p className="text-sm text-muted-foreground">Acquisition channels by visitors and sales.</p>
          </div>
          <div className="flex-1 px-6 py-5">
            {data?.topSources?.length ? (
              <ul className="space-y-3 text-sm">
                {data.topSources.map((source) => (
                  <li key={source.source} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/30 px-4 py-3 dark:bg-white/5">
                    <div>
                      <p className="font-medium text-foreground">{source.source}</p>
                      <p className="text-xs text-muted-foreground">{numberFormatter.format(source.visitors)} visitors</p>
                    </div>
                    <span className="text-sm font-semibold text-primary">{currencyFormatter.format(source.sales)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground">No data to display</p>
                <p className="mt-2">Connect marketing sources to populate this view.</p>
              </div>
            )}
          </div>
        </article>
      </section>

      {data?.notes && (
        <section className="glass-card p-6 text-sm text-muted-foreground">
          <p>{data.notes}</p>
        </section>
      )}
    </div>
  );
}

